import { db } from '../../data/base';
import { collection, addDoc, doc, getDoc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { secureGetItem } from '../../data/utils';
import {
    getCatalogue,
    saveCatalogue,
    updateCatalogue,
    prepareAssetsAndSave,
    mapFormDataToCatalogue,
    sanitizeForFirestore,
    updateUserDraftedCatalogues
} from './catalogueBaseService';
import { updateDraftReference, removeDraftReference } from './catalogueService';
import { mapFormDataToDraftedCatalogue } from './catalogueDraftService';

/**
 * Ottiene una bozza dal database
 * @param {string} draftId - L'ID della bozza
 * @returns {Promise<object|null>} - I dati della bozza o null se non esiste
 */
export async function getDraftedCatalogue(draftId) {
    return getCatalogue('DraftedCatalogues', draftId);
}

/**
 * Salva una nuova bozza
 * @param {object} formData - I dati del form
 * @returns {Promise<string>} - L'ID della bozza salvata
 */
export async function saveDraftedCatalogue(formData) {
    const options = {
        isDraft: true,
        draftCreatedAt: new Date().toISOString()
    };

    const draftId = await saveCatalogue('DraftedCatalogues', formData, options);
    return draftId;
}

/**
 * Aggiorna una bozza esistente
 * @param {string} draftId - L'ID della bozza
 * @param {object} formData - I dati del form
 * @returns {Promise<string>} - L'ID della bozza aggiornata
 */
export async function updateDraftedCatalogue(draftId, formData) {
    return updateCatalogue('DraftedCatalogues', draftId, formData);
}

/**
 * Salva un catalogo pubblicato come bozza
 * @param {string} originalCatalogueId - L'ID del catalogo pubblicato originale
 * @param {object} formData - I dati del form con le modifiche dell'utente
 * @returns {Promise<string>} - L'ID della bozza creata
 */
export async function savePublishedCatalogAsDraft(originalCatalogueId, formData) {
    try {
        if (!originalCatalogueId) {
            throw new Error('Original catalogue ID is required');
        }

        // Ottieni i dati del catalogo originale
        const catalogueRef = doc(db, "Catalogues", originalCatalogueId);
        const catalogueSnap = await getDoc(catalogueRef);

        if (!catalogueSnap.exists()) {
            throw new Error(`Original catalogue with ID ${originalCatalogueId} not found`);
        }

        const catalogueData = catalogueSnap.data();

        // Prepara i dati del form aggiungendo l'ID del catalogo originale
        const readyFormData = {
            ...formData,
            originalCatalogueId: originalCatalogueId
        };

        // Prepara gli asset (carica immagini, ecc.)
        const processedFormData = await prepareAssetsAndSave(readyFormData);

        // Rimuovi esplicitamente qualsiasi oggetto File dal seo.featuredImage
        if (processedFormData.seo && processedFormData.seo.featuredImage) {
            if (typeof processedFormData.seo.featuredImage === 'object') {
                if (processedFormData.seo.featuredImage.url) {
                    processedFormData.seo.featuredImage = processedFormData.seo.featuredImage.url;
                } else if (processedFormData.seo.featuredImage.file) {
                    delete processedFormData.seo.featuredImage;
                }
            }
        }

        // Mappa i dati nella struttura JSON standard usando il mapping che funziona
        const mappedData = mapFormDataToDraftedCatalogue(processedFormData);

        // CRITICAL FIX: For published catalog modifications, preserve original data structure
        // and only update the fields that were actually modified in the stepper
        const { draftedCatalogRef, ...catalogueDataWithoutDraftRef } = catalogueData;
        const draftData = {
            ...sanitizeForFirestore(catalogueDataWithoutDraftRef), // Start with complete original catalog but exclude draftedCatalogRef
            // Only update specific fields that can be modified in the stepper
            nameCatalog: mappedData.nameCatalog || catalogueData.nameCatalog,
            webDomain: mappedData.webDomain || catalogueData.webDomain,
            descriptionEn: mappedData.descriptionEn || catalogueData.descriptionEn,
            featuredImage: mappedData.featuredImage || catalogueData.featuredImage,
            isPublic: mappedData.isPublic !== null ? mappedData.isPublic : catalogueData.isPublic,
            // Update catalogues_page_style by merging with original
            catalogues_page_style: {
                ...catalogueData.catalogues_page_style, // Preserve all original styling
                // Only update the fields that can be modified in the stepper
                ...(mappedData.catalogues_page_style && {
                    primaryColor: mappedData.catalogues_page_style.primaryColor || catalogueData.catalogues_page_style?.primaryColor,
                    secondaryColor: mappedData.catalogues_page_style.secondaryColor || catalogueData.catalogues_page_style?.secondaryColor,
                    backgroundColor: mappedData.catalogues_page_style.backgroundColor || catalogueData.catalogues_page_style?.backgroundColor,
                    actionButtonColor: mappedData.catalogues_page_style.actionButtonColor || catalogueData.catalogues_page_style?.actionButtonColor,
                    bannerBackgroundColor: mappedData.catalogues_page_style.bannerBackgroundColor || catalogueData.catalogues_page_style?.bannerBackgroundColor,
                    // Border radius values
                    cardCornerTopLeft: mappedData.catalogues_page_style.cardCornerTopLeft ?? catalogueData.catalogues_page_style?.cardCornerTopLeft,
                    cardCornerTopRight: mappedData.catalogues_page_style.cardCornerTopRight ?? catalogueData.catalogues_page_style?.cardCornerTopRight,
                    cardCornerBottomRight: mappedData.catalogues_page_style.cardCornerBottomRight ?? catalogueData.catalogues_page_style?.cardCornerBottomRight,
                    cardCornerBottomLeft: mappedData.catalogues_page_style.cardCornerBottomLeft ?? catalogueData.catalogues_page_style?.cardCornerBottomLeft,
                    buttonCornerTopLeft: mappedData.catalogues_page_style.buttonCornerTopLeft ?? catalogueData.catalogues_page_style?.buttonCornerTopLeft,
                    buttonCornerTopRight: mappedData.catalogues_page_style.buttonCornerTopRight ?? catalogueData.catalogues_page_style?.buttonCornerTopRight,
                    buttonCornerBottomRight: mappedData.catalogues_page_style.buttonCornerBottomRight ?? catalogueData.catalogues_page_style?.buttonCornerBottomRight,
                    buttonCornerBottomLeft: mappedData.catalogues_page_style.buttonCornerBottomLeft ?? catalogueData.catalogues_page_style?.buttonCornerBottomLeft,
                    bannerCornerTopLeft: mappedData.catalogues_page_style.bannerCornerTopLeft ?? catalogueData.catalogues_page_style?.bannerCornerTopLeft,
                    bannerCornerTopRight: mappedData.catalogues_page_style.bannerCornerTopRight ?? catalogueData.catalogues_page_style?.bannerCornerTopRight,
                    bannerCornerBottomRight: mappedData.catalogues_page_style.bannerCornerBottomRight ?? catalogueData.catalogues_page_style?.bannerCornerBottomRight,
                    bannerCornerBottomLeft: mappedData.catalogues_page_style.bannerCornerBottomLeft ?? catalogueData.catalogues_page_style?.bannerCornerBottomLeft,
                    // Banner settings
                    catalogueLogo: mappedData.catalogues_page_style.catalogueLogo || catalogueData.catalogues_page_style?.catalogueLogo,
                    catalogueLogoPosition: mappedData.catalogues_page_style.catalogueLogoPosition || catalogueData.catalogues_page_style?.catalogueLogoPosition,
                    coverHeightDesktop: mappedData.catalogues_page_style.coverHeightDesktop || catalogueData.catalogues_page_style?.coverHeightDesktop,
                    coverHeightMobile: mappedData.catalogues_page_style.coverHeightMobile || catalogueData.catalogues_page_style?.coverHeightMobile,
                    coverImg: mappedData.catalogues_page_style.coverImg || catalogueData.catalogues_page_style?.coverImg,
                    coverImgMobile: mappedData.catalogues_page_style.coverImgMobile || catalogueData.catalogues_page_style?.coverImgMobile,
                    isLogoVisible: mappedData.catalogues_page_style.isLogoVisible !== null ? mappedData.catalogues_page_style.isLogoVisible : catalogueData.catalogues_page_style?.isLogoVisible,
                    isTitleVisible: mappedData.catalogues_page_style.isTitleVisible !== null ? mappedData.catalogues_page_style.isTitleVisible : catalogueData.catalogues_page_style?.isTitleVisible,
                    heightOption: mappedData.catalogues_page_style.heightOption || catalogueData.catalogues_page_style?.heightOption,
                    heightMobileOption: mappedData.catalogues_page_style.heightMobileOption || catalogueData.catalogues_page_style?.heightMobileOption
                })
            },
            // Update urlLogo if banner logo was modified
            urlLogo: mappedData.urlLogo || catalogueData.urlLogo,
            // Draft-specific fields
            isDraft: true,
            originalCatalogueId: originalCatalogueId,
            draftCreatedAt: new Date().toISOString()
            // Remove userModifications object - not needed in final structure
        };

        // Salva il documento nella collezione DraftedCatalogues
        const docRef = await addDoc(collection(db, 'DraftedCatalogues'), draftData);
        const draftId = docRef.id;

        // Aggiorna la lista dei drafted catalogues nel profilo utente
        await updateUserDraftedCatalogues(draftId);

        // Aggiorna il riferimento alla bozza nel catalogo originale
        await updateDraftReference(originalCatalogueId, draftId);

        return draftId;
    } catch (error) {
        console.error("Error saving published catalog as draft:", error);
        throw new Error(`Failed to save draft: ${error.message}`);
    }
}

/**
 * Pubblica una bozza come catalogo
 * @param {string} draftId - L'ID della bozza
 * @returns {Promise<string>} - L'ID del catalogo pubblicato
 */
export async function publishDraft(draftId) {
    try {
        // Ottieni i dati della bozza
        const draftData = await getDraftedCatalogue(draftId);

        if (!draftData) {
            throw new Error(`Draft with ID ${draftId} not found`);
        }

        // Rimuovi i campi specifici delle bozze
        const { isDraft, draftCreatedAt, userModifications, originalCatalogueId, ...catalogueData } = draftData;

        // Se è una modifica di un catalogo esistente, aggiorna il catalogo
        if (originalCatalogueId) {
            await updateDoc(doc(db, 'Catalogues', originalCatalogueId), catalogueData);

            // Rimuovi il riferimento alla bozza
            await removeDraftReference(originalCatalogueId);

            // Elimina la bozza
            await deleteDoc(doc(db, 'DraftedCatalogues', draftId));

            // Rimuovi la bozza dalla lista dell'utente
            await removeFromUserDraftedCatalogues(draftId);

            return originalCatalogueId;
        } else {
            // Se è una nuova bozza, crea un nuovo catalogo
            const docRef = await addDoc(collection(db, 'Catalogues'), catalogueData);
            const catalogueId = docRef.id;

            // Elimina la bozza
            await deleteDoc(doc(db, 'DraftedCatalogues', draftId));

            // Rimuovi la bozza dalla lista dell'utente
            await removeFromUserDraftedCatalogues(draftId);

            return catalogueId;
        }
    } catch (error) {
        console.error('Error publishing draft:', error);
        throw error;
    }
}

/**
 * Rimuove una bozza dalla lista dell'utente
 * @param {string} draftId - L'ID della bozza
 * @returns {Promise<void>}
 */
export async function removeFromUserDraftedCatalogues(draftId) {
    try {
        let email = secureGetItem('email');

        if (email) {
            email = email.toLowerCase();
            const profileRef = doc(db, 'Profiles', email);
            const profileSnap = await getDoc(profileRef);

            if (profileSnap.exists()) {
                const profileData = profileSnap.data();
                let listDrafted = Array.isArray(profileData.list_drafted_catalogues)
                    ? profileData.list_drafted_catalogues
                    : [];

                // Rimuovi la bozza dalla lista
                listDrafted = listDrafted.filter(id => id !== draftId);

                await setDoc(profileRef, { list_drafted_catalogues: listDrafted }, { merge: true });
            }
        }
    } catch (error) {
        console.error('Error removing draft from user list:', error);
    }
}

/**
 * Ottiene tutte le bozze dell'utente corrente
 * @returns {Promise<Array>} - Array di bozze
 */
export async function getUserDraftedCatalogues() {
    try {
        let email = secureGetItem('email');

        if (!email) {
            return [];
        }

        email = email.toLowerCase();
        const profileRef = doc(db, 'Profiles', email);
        const profileSnap = await getDoc(profileRef);

        if (!profileSnap.exists()) {
            return [];
        }

        const profileData = profileSnap.data();
        const listDrafted = Array.isArray(profileData.list_drafted_catalogues)
            ? profileData.list_drafted_catalogues
            : [];

        if (listDrafted.length === 0) {
            return [];
        }

        // Ottieni i dati di tutte le bozze
        const drafts = [];

        for (const draftId of listDrafted) {
            const draft = await getDraftedCatalogue(draftId);

            if (draft) {
                drafts.push(draft);
            }
        }

        return drafts;
    } catch (error) {
        console.error('Error getting user drafted catalogues:', error);
        return [];
    }
}
